import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance, { isAxiosError } from "@/utils/axiosInstance";

export const deleteSubscriptionUsage = createAsyncThunk(
  "subscriptionUsage/delete",
  async (
    {
      establishmentId,
      subscriptionId,
      clientSubscriptionId,
      usageId,
    }: {
      establishmentId: string | number;
      subscriptionId: string | number;
      clientSubscriptionId: string | number;
      usageId: string | number;
    },
    thunkAPI,
  ) => {
    try {
      const attemptedUrls: string[] = [];

      // Helper to try deleting a URL and return true if succeeded
      const tryDelete = async (url: string) => {
        attemptedUrls.push(url);
        try {
          // Debug: log the attempt
          // eslint-disable-next-line no-console
          console.debug("Attempting DELETE", { url });
          await axiosInstance.delete(url);
          return true;
        } catch (err) {
          // If 404, we'll try next candidate. Otherwise rethrow.
          if (isAxiosError(err) && err.response?.status === 404) {
            return false;
          }
          throw err;
        }
      };

      // Primary: subscription + client + usage
      if (subscriptionId && clientSubscriptionId && usageId) {
        let url = `/subscriptions/${subscriptionId}/clients/${clientSubscriptionId}/usage/${usageId}`;
        const isEstablishmentSlug =
          establishmentId &&
          typeof establishmentId === "string" &&
          /[a-zA-Z]/.test(String(establishmentId));
        if (isEstablishmentSlug) {
          url = `/establishment/${establishmentId}${url}`;
        }
        if (await tryDelete(url)) return { usageId } as any;
      }

      // Secondary fallbacks (try commonly used patterns)
      const candidates = [] as string[];
      if (usageId) {
        candidates.push(`/subscriptions/usage/${usageId}`);
        candidates.push(`/usage/${usageId}`);
        candidates.push(`/subscriptions/usage/${usageId}`);
        if (establishmentId && typeof establishmentId === "string") {
          candidates.push(
            `/establishment/${establishmentId}/subscriptions/usage/${usageId}`,
          );
          candidates.push(`/establishment/${establishmentId}/usage/${usageId}`);
        }
      }

      for (const c of candidates) {
        if (await tryDelete(c)) return { usageId } as any;
      }

      return thunkAPI.rejectWithValue({
        message:
          "Não foi possível excluir a utilização: nenhum endpoint compatível respondeu. URLs tentadas: " +
          attemptedUrls.join(", "),
      });
    } catch (error) {
      if (isAxiosError(error)) {
        const backend = error.response?.data as {
          message?: string;
          errors?: any;
        };
        return thunkAPI.rejectWithValue({
          message: backend?.message || "Erro ao excluir utilização.",
          errors: backend?.errors || {},
        });
      }
      return thunkAPI.rejectWithValue({
        message: "Erro inesperado ao excluir utilização.",
        errors: {},
      });
    }
  },
);
